import { extractCelebritiesFromText, getCelebrityBaikeUrl } from "@/config/celebrities";
import { getCelebrityAvatar } from "@/config/celebrity-avatars";
import { extractCitiesFromText } from "@/config/cities";
import { extractCountriesFromText } from "@/config/countries";
import { containsHotEntityName, extractHotEntitiesFromText } from "@/config/hot-entities";

export interface TopicLike {
  id: number;
  name: string;
}

export interface TopicAffinityLabel {
  type: "person" | "city" | "country" | "program" | "event" | "organization" | "keyword";
  name: string;
  meta: string;
  href: string;
  icon?: string;
  avatarUrl?: string;
  baikeUrl?: string;
}

export interface RelatedTopicLink {
  id: number;
  name: string;
  reason: string;
}

export type TopicWithAffinity<T extends TopicLike> = T & {
  affinityLabels: TopicAffinityLabel[];
  relatedTopics: RelatedTopicLink[];
};

function labelsForName(name: string): TopicAffinityLabel[] {
  const hotEntityLabels = extractHotEntitiesFromText(name).slice(0, 3).map((entity) => ({
    type: entity.type,
    name: entity.name,
    meta: entity.meta,
    href: `/search?q=${encodeURIComponent(entity.name)}`,
  }));

  const countryLabels = extractCountriesFromText(name).slice(0, 2).map((country) => ({
    type: "country" as const,
    name: country.name,
    meta: country.region,
    href: `/country/${encodeURIComponent(country.name)}`,
    icon: country.flag,
  }));

  const cityLabels = extractCitiesFromText(name).slice(0, 2).map((city) => ({
    type: "city" as const,
    name: city.name,
    meta: city.province === city.name ? city.category : `${city.province} · ${city.category}`,
    href: `/city/${encodeURIComponent(city.name)}`,
  }));

  const celebrityLabels = extractCelebritiesFromText(name)
    .filter((celebrity) => !containsHotEntityName(celebrity.name))
    .filter((celebrity) => celebrity.gender !== "未知")
    .slice(0, 2)
    .map((celebrity) => {
      const trimmed = celebrity.name.trim();
      return {
        type: "person" as const,
        name: trimmed,
        meta: celebrity.category,
        href: `/celebrity/${encodeURIComponent(trimmed)}`,
        avatarUrl: getCelebrityAvatar(trimmed)?.url,
        baikeUrl: getCelebrityBaikeUrl(trimmed),
      };
    });

  return [...celebrityLabels, ...hotEntityLabels, ...cityLabels, ...countryLabels];
}

export function getAffinityLabelsForTopic(name: string): TopicAffinityLabel[] {
  return labelsForName(name);
}

export function getAffinityTypeName(type: TopicAffinityLabel["type"]): string {
  if (type === "person") return "名人";
  if (type === "city") return "城市";
  if (type === "country") return "国家";
  if (type === "program") return "节目";
  if (type === "event") return "事件";
  if (type === "organization") return "组织";
  return "热点词";
}

export function enhanceTopicItems<T extends TopicLike>(items: T[]): Array<TopicWithAffinity<T>> {
  const labeled = items.map((item) => ({
    ...item,
    affinityLabels: labelsForName(item.name),
    relatedTopics: [] as RelatedTopicLink[],
  }));

  const byLabel = new Map<string, Array<TopicWithAffinity<T>>>();

  for (const item of labeled) {
    for (const label of item.affinityLabels) {
      const key = `${label.type}:${label.name}`;
      const list = byLabel.get(key) || [];
      list.push(item);
      byLabel.set(key, list);
    }
  }

  return labeled.map((item) => {
    const related = new Map<number, RelatedTopicLink>();

    for (const label of item.affinityLabels) {
      const list = byLabel.get(`${label.type}:${label.name}`) || [];
      for (const candidate of list) {
        if (candidate.id === item.id || related.has(candidate.id)) continue;
        related.set(candidate.id, {
          id: candidate.id,
          name: candidate.name,
          reason:
            label.type === "city"
              ? `同城 · ${label.name}`
              : label.type === "person"
              ? `同名人 · ${label.name}`
              : label.type === "country"
              ? `同国家 · ${label.name}`
              : `同${getAffinityTypeName(label.type)} · ${label.name}`,
        });
        if (related.size >= 3) break;
      }
      if (related.size >= 3) break;
    }

    return {
      ...item,
      relatedTopics: Array.from(related.values()),
    };
  });
}
